import React from 'react'
import { OutsideHoursError, ValidationError, api, type Appointment, type Plan } from '../../api/client'
import { useApp } from '../../context/AppContext'
import { computeForecast, type Forecast, type Stretch } from '../../plan/forecast'
import { goalsDraft, shouldRestoreWeekDraft } from '../../plan/goalsDraft'
import type { GoalDraft } from '../../plan/planForm'
import { CLIENTS_CAN_BOOK, DREAM_WEEK_SAVED } from '../../plan/wording'
import { DraftWeek, type DraftWindow, errorsByWindow, sameWeek, toDraftWeek, toWindows } from '../settings/hoursForm'
import { saveErrorMessage, showToast } from '../settings/toast'

interface Args {
  plan: Plan
  goal: GoalDraft
  // onPlanSaved tells Goals the plan the server now holds, so a later failure
  // (e.g. applying outside hours) never leaves Goals holding a stale plan.
  onPlanSaved: (plan: Plan) => void
  // onInvalidDay is told the first day with a stretch that couldn't be saved,
  // so the screen can open it: errors only show in the open day's editor.
  onInvalidDay?: (day: number) => void
}

export interface PlanWeek {
  week: DraftWeek
  liveWindows: Stretch[]
  forecast: Forecast
  errors: Record<string, string>
  error: string
  unsaved: boolean
  busy: 'save' | 'apply' | null
  outside: Appointment[] | null
  edit: (next: DraftWeek) => void
  keepAsPlan: () => Promise<void>
  useAsHours: (force: boolean) => Promise<void>
  startFromCurrentHours: () => Promise<void>
  dismissOutside: () => void
}

// usePlanWeek holds the week she is painting: its draft (saved on every edit,
// so leaving the screen costs nothing), the forecast it adds up to, and
// saving it as a plan or as her working hours.
export function usePlanWeek({ plan, goal, onPlanSaved, onInvalidDay }: Args): PlanWeek {
  const { dispatch } = useApp()
  const [week, setWeek] = React.useState<DraftWeek>(() => {
    const saved = goalsDraft.week()
    const baseline = toDraftWeek(plan.windows)
    // A draft only belongs here if the plan it was edited from is still the
    // plan we're looking at. Otherwise the hours moved on underneath it
    // (saved elsewhere, or from another tab), and reopening it would silently
    // overwrite those newer hours.
    if (saved && shouldRestoreWeekDraft(saved.base, baseline)) return saved.draft
    if (saved) goalsDraft.clearWeek()
    return baseline
  })
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState<'save' | 'apply' | null>(null)
  const [error, setError] = React.useState('')
  const [outside, setOutside] = React.useState<Appointment[] | null>(null)

  // A stretch still being typed is left out of the forecast rather than
  // stopping it: the numbers keep up with every complete stretch. That
  // includes one whose end hasn't caught up with its start yet — half typed,
  // it must not lower hours or double capacity.
  const { windows } = toWindows(week)
  const liveWindows = windows.filter((w) => w.end_minute > w.start_minute)
  const forecast = computeForecast(liveWindows, plan.inputs, goal)
  const unsaved = plan.goal === null || !sameWeek(week, toDraftWeek(plan.windows))

  const edit = (next: DraftWeek) => {
    // A day she has just changed no longer holds the stretch that was turned
    // down, so its old errors go; other days keep theirs until fixed.
    setErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => {
          const day = Number(key.split(':')[0])
          return key === 'week' || sameDay(next[day], week[day])
        }),
      ),
    )
    setWeek(next)
    goalsDraft.saveWeek(next, toDraftWeek(plan.windows))
    setOutside(null)
  }

  const showErrors = (next: Record<string, string>) => {
    setErrors(next)
    const days = Object.keys(next)
      .filter((key) => key !== 'week')
      .map((key) => Number(key.split(':')[0]))
    if (days.length > 0) onInvalidDay?.(Math.min(...days))
  }

  const save = async (): Promise<Plan | null> => {
    const draft = toWindows(week)
    if (Object.keys(draft.errors).length > 0) {
      showErrors(draft.errors)
      return null
    }
    setErrors({})
    try {
      return await api.putPlan({ ...goal, windows: draft.windows })
    } catch (e) {
      if (e instanceof ValidationError) {
        showErrors(errorsByWindow(e.fields, draft.positions))
        const goalProblem = Object.entries(e.fields).find(([field]) => !field.startsWith('windows'))
        if (goalProblem) setError(goalProblem[1])
      } else {
        setError(saveErrorMessage(e))
      }
      return null
    }
  }

  const keepAsPlan = async () => {
    setBusy('save')
    setError('')
    const next = await save()
    setBusy(null)
    if (next) {
      goalsDraft.clearWeek()
      showToast(dispatch, DREAM_WEEK_SAVED)
      onPlanSaved(next)
    }
  }

  const useAsHours = async (force: boolean) => {
    setBusy('apply')
    setError('')
    try {
      const next = unsaved ? await save() : plan
      if (!next) return
      onPlanSaved(next)
      const applied = await api.applyPlan(force)
      goalsDraft.clearWeek()
      setOutside(null)
      showToast(dispatch, CLIENTS_CAN_BOOK)
      // The applied windows are now her working hours too, so the plan is
      // live: without this, the overview keeps saying "not your booking
      // hours yet" until the next full fetch.
      onPlanSaved({ ...next, windows: applied, live: true })
    } catch (e) {
      if (e instanceof OutsideHoursError) {
        setOutside(e.appointments)
      } else {
        setError(saveErrorMessage(e))
      }
    } finally {
      setBusy(null)
    }
  }

  const startFromCurrentHours = async () => {
    setError('')
    try {
      edit(toDraftWeek(await api.getWorkingHours()))
    } catch (e) {
      setError(saveErrorMessage(e))
    }
  }

  return {
    week,
    liveWindows,
    forecast,
    errors,
    error,
    unsaved,
    busy,
    outside,
    edit,
    keepAsPlan,
    useAsHours,
    startFromCurrentHours,
    dismissOutside: () => setOutside(null),
  }
}

// sameDay reports whether two drafts of one day hold the same stretches.
const sameDay = (a: DraftWindow[], b: DraftWindow[]) =>
  a.length === b.length && a.every((w, i) => w.start === b[i].start && w.end === b[i].end)
